import React, { Component, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import "../styles/Home.css";
import reviews from "./Review.json";

// ErrorBoundary component to handle unexpected errors gracefully
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    if (typeof window.gtag === "function") {
      window.gtag("event", "exception", {
        description: error.toString(),
        fatal: false,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="home-fallback">
          <h1>Something went wrong. Please try again later.</h1>
          <a href="/" className="home-fallback-link">
            Go back to Home
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function useVisibleCards() {
  const [visible, setVisible] = useState(3);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mqMobile = window.matchMedia("(max-width: 640px)");
    const mqTablet = window.matchMedia("(max-width: 980px)");

    const compute = () => {
      // Show two reviews on narrow/mobile screens for better discovery
      if (mqMobile.matches) return setVisible(2);
      if (mqTablet.matches) return setVisible(2);
      return setVisible(3);
    };

    compute();

    const add = (mq, fn) => {
      if (mq.addEventListener) mq.addEventListener("change", fn);
      else mq.addListener(fn);
    };
    const remove = (mq, fn) => {
      if (mq.removeEventListener) mq.removeEventListener("change", fn);
      else mq.removeListener(fn);
    };

    add(mqMobile, compute);
    add(mqTablet, compute);

    return () => {
      remove(mqMobile, compute);
      remove(mqTablet, compute);
    };
  }, []);

  return visible;
}

function Home() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const VISIBLE = useVisibleCards();
  const total = Array.isArray(reviews) ? reviews.length : 0;

  const safeIndex = useMemo(() => {
    if (!total) return 0;
    return ((index % total) + total) % total;
  }, [index, total]);

  const next = () => {
    if (!total) return;
    setIndex((prev) => (prev + 1) % total);
  };

  const prev = () => {
    if (!total) return;
    setIndex((prev) => (prev - 1 + total) % total);
  };

  // Auto rotate every 6 seconds (pauses on hover/focus)
  useEffect(() => {
    if (!total) return;
    if (paused) return;

    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);

    return () => clearInterval(timer);
  }, [paused, total]);

  const visibleReviews = useMemo(() => {
    if (!total) return [];
    return Array.from({ length: VISIBLE }).map((_, i) => reviews[(safeIndex + i) % total]);
  }, [VISIBLE, safeIndex, total]);

  return (
    <ErrorBoundary>
      <div className="home-container">
        <Helmet>
          <title>Lash Extensions in Rochester, NY | Lash with Brittany</title>
          <meta
            name="description"
            content="Wake up flawless with lash extensions in Rochester, NY from Lash with Brittany. Reserve your eyelash extension appointment online today."
          />
          <meta
            name="keywords"
            content="lash extensions Rochester NY, eyelash extensions Rochester NY, lash studio Rochester NY, lash artist Rochester NY, classic lash extensions, hybrid lash extensions, volume lash extensions, lash fills and refills, professional lash extensions"
          />
          <meta name="robots" content="index,follow" />

          <meta property="og:type" content="website" />
          <meta property="og:title" content="Lash Extensions in Rochester, NY | Lash with Brittany" />
          <meta
            property="og:description"
            content="Wake up flawless with professional lash extensions from Brittany, a licensed and certified lash artist in Rochester, NY."
          />
          <meta property="og:url" content="https://lashwithbrittany.com" />
          <meta property="og:image" content="https://lashwithbrittany.com/hero-bg-square.png" />
          <meta property="og:image:alt" content="Lash extensions by Lash with Brittany" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Lash with Brittany - Eyelash Extensions in Rochester, NY" />
          <meta
            name="twitter:description"
            content="Get professional eyelash extensions from Lash with Brittany in Rochester, NY. Book your appointment now to transform your look."
          />
          <meta name="twitter:image" content="https://lashwithbrittany.com/images/lash1.jpg" />
          <link rel="canonical" href="https://lashwithbrittany.com" />
        </Helmet>

        <main className="home-main">
          {/* ---------- HERO ---------- */}
          <section className="home-hero-frame hero-no-image" id="hero" aria-label="Lash extensions hero">
            <div className="home-hero hero-centered">
              <div className="home-hero-text">
                <p className="hero-kicker">Trusted by clients since 2018</p>

                <h1>Lash Extensions in Rochester, NY with Lash with Brittany</h1>

                <p className="hero-subheading">
                  Wake up flawless with long lasting eyelash extensions tailored for your
                  vibe, from soft and classic to full volume.
                </p>

                <ul className="hero-highlights">
                  <li>Customized classic, hybrid, and volume lash extensions in Rochester, NY.</li>
                  <li>Licensed lash artist delivering professional lash extensions and relaxing fills.</li>
                  <li>Online scheduling for lash extensions and refills whenever you are ready.</li>
                </ul>

                <div className="hero-cta-group">
                  <a
                    href="/book"
                    className="cta-button primary"
                    aria-label="Book your lash extensions in Rochester, NY"
                  >
                    Book Now
                  </a>

                  <a
                    href="/pricelist"
                    className="cta-button secondary"
                    aria-label="Explore lash extension services in Rochester, NY"
                  >
                    Explore Services
                  </a>
                </div>

                <div className="hero-quicklinks" aria-label="Quick links">
                  <a className="quicklink" href="/gallery">View Gallery</a>
                  <a className="quicklink" href="/permanent-jewelry">Permanent Jewelry</a>
                  <a className="quicklink" href="/giftcards">Gift Cards</a>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- TRUST BANNER ---------- */}
          <section className="trust-banner" aria-label="Lash extensions booking banner">
            <p>Wake up flawless with long lasting lash extensions. Secure your appointment in Rochester, NY today.</p>
            <a href="/book" className="cta-button primary" aria-label="Schedule lash extensions in Rochester, NY">
              Book Now
            </a>
          </section>

          {/* ---------- SERVICES ---------- */}
          <section className="services-highlight" id="services" aria-labelledby="services-heading">
            <div className="home-section-head">
              <h2 id="services-heading">Signature Lash Extension Services in Rochester, NY</h2>
              <p className="home-section-sub">
                Choose your vibe. Every set is custom fit to your eye shape and your lifestyle.
              </p>
            </div>

            <div className="service-grid">
              <article className="service-card">
                <h3>Classic Lash Extensions</h3>
                <p>
                  Enhance your natural lashes with a mascara free finish using professional classic lash extensions
                  applied one to one for soft definition.
                </p>
              </article>

              <article className="service-card">
                <h3>Hybrid Lash Extensions</h3>
                <p>
                  Blend classic and volume techniques for a textured set that adds dimension and fullness without
                  sacrificing comfort.
                </p>
              </article>

              <article className="service-card">
                <h3>Volume Lash Extensions</h3>
                <p>
                  Turn up the drama with handcrafted volume fans tailored to your eye shape for bold, ultra fluffy
                  lashes.
                </p>
              </article>

              <article className="service-card">
                <h3>Lash Fills and Refills</h3>
                <p>
                  Keep your lashes fresh with regular lash fills that maintain fullness and protect the health of your
                  natural lashes.
                </p>
              </article>
            </div>

            <a href="/pricelist" className="cta-link" aria-label="View all lash extension services">
              See full service menu
            </a>
          </section>

          {/* ---------- ABOUT ---------- */}
          <section className="about-section" id="about" aria-labelledby="about-heading">
            <div className="about-frame">
              <div className="about-card">
                <h2 id="about-heading">About Brittany</h2>

                <p>
                  Hi, I’m Brittany. I’m a licensed esthetician, boy mom, elder emo,
                  <span className="highlight"> Buffalo Bills superfan</span>, and your new favorite lash tech. I have
                  been lashing since 2018 and specialize in creating custom lash looks for every vibe, from natural to
                  dramatic and everything in between. My passion for lashes started at the Continental School of Beauty
                  here in Rochester, and I have loved it ever since.
                </p>

                <p>
                  My suite is a no pressure, come as you are comfort zone where you can nap, vent, talk about music, or
                  just relax with some checkered vibes. I am also obsessed with permanent jewelry, good playlists, viral
                  TikToks, and my <span className="highlight">two golden retrievers</span>.
                </p>

                <p>
                  Whether you are nineteen or sixty five, in scrubs or sweats, I have you covered. You will leave
                  feeling seen, listened to, and looking like <span className="highlight">that girl</span>.
                </p>

                <p>
                  Ready to join the best girl gang in Rochester? Book your appointment today and let’s make magic
                  happen.
                </p>

                <p className="about-signoff">
                  <strong>Lash With Brittany</strong>
                </p>

                <div className="about-cta-row">
                  <a href="/book" className="cta-button primary" aria-label="Book now">
                    Book Now
                  </a>
                  <a href="/gallery" className="cta-button secondary" aria-label="View gallery">
                    See Results
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- BENEFITS ---------- */}
          <section className="benefits-section" aria-labelledby="benefits-heading">
            <div className="home-section-head">
              <h2 id="benefits-heading">Why Rochester Clients Love Lash with Brittany</h2>
              <p className="home-section-sub">The details matter. Comfort, retention, and a set that fits your face.</p>
            </div>

            <ul className="benefits-list">
              <li>Licensed lash artist delivering customized lash extensions in Rochester, NY.</li>
              <li>Premium products designed for sensitive eyes and long lasting retention.</li>
              <li>Relaxing lash studio in Rochester with curated playlists and cozy vibes.</li>
            </ul>

            <a href="/book" className="cta-button primary" aria-label="Book lash extensions in Rochester">
              Reserve Your Spot
            </a>
          </section>

          {/* ---------- REVIEWS ---------- */}
          <section className="reviews-section" aria-labelledby="reviews-heading">
            <div className="home-section-head">
              <h2 id="reviews-heading">What Clients Are Saying</h2>
              <p className="home-section-sub">Real feedback from clients who came for lashes and stayed for the vibes.</p>
              <a href="/reviews" className="reviews-link" aria-label="See all client reviews">See all reviews</a>
            </div>

            <div
              className="reviews-carousel"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocusCapture={() => setPaused(true)}
              onBlurCapture={() => setPaused(false)}
            >
              <button className="carousel-btn" onClick={prev} aria-label="Previous reviews" type="button">
                ‹
              </button>

              <div className="reviews-track" style={{ "--visible": VISIBLE }}>
                {visibleReviews.map((r, i) => (
                  <div className="review-card" key={`${r?.name || "review"}-${i}`}>
                    <div className="review-top">
                      <div className="review-rating" aria-label={`${r.rating} star rating`}>
                        {"★".repeat(r.rating)}
                      </div>
                      <div className="review-chip">{r.source === "google" ? "Google" : "Ovatu"}</div>
                    </div>

                    <p className="review-text">{r.text}</p>

                    <p className="review-name">
                      <span className="review-dash">—</span> {r.name}
                    </p>
                  </div>
                ))}
              </div>

              <button className="carousel-btn" onClick={next} aria-label="Next reviews" type="button">
                ›
              </button>
            </div>
          </section>

          {/* ---------- FAQ ---------- */}
          <section className="faq-section" aria-labelledby="faq-heading">
            <div className="home-section-head">
              <h2 id="faq-heading">Rochester Lash Extension FAQs</h2>
              <p className="home-section-sub">Quick answers so you can book with confidence.</p>
            </div>

            <div className="faq-grid">
              <article className="faq-card">
                <h3>How long do professional lash extensions last?</h3>
                <p>
                  With proper aftercare, your lash extensions can last 4 to 6 weeks. Book lash fills every 2 to 3 weeks
                  to keep your set looking full.
                </p>
              </article>

              <article className="faq-card">
                <h3>What should I do before my lash extension appointment?</h3>
                <p>
                  Arrive with clean lashes and avoid oil based products. This helps your lash artist create a strong bond
                  for better retention.
                </p>
              </article>

              <article className="faq-card">
                <h3>Can I switch between classic and volume lash extensions?</h3>
                <p>
                  Yes. We can tailor each fill to match your preferred style, whether you love natural classics or full
                  volume.
                </p>
              </article>
            </div>
          </section>

          {/* ---------- LOCAL SEO ---------- */}
          <section className="local-seo" aria-labelledby="service-area-heading">
            <h2 id="service-area-heading">Serving Rochester, NY and the Greater Finger Lakes Region</h2>
            <p>
              Lash with Brittany is a boutique lash studio in Rochester, NY proudly serving clients throughout Brighton,
              Pittsford, Webster, Irondequoit, Fairport, and the greater Finger Lakes region. Whether you are searching
              for eyelash extensions in Rochester, NY or need a trusted lash artist near you for fills and refills, you
              will feel at home here.
            </p>
            <p>
              Book online to secure your appointment and experience professional lash extensions designed to help you
              wake up flawless every day.
            </p>
          </section>
        </main>
      </div>
    </ErrorBoundary>
  );
}

export default Home;
